import React, { useState } from "react";
import { CiLock } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import "../../assets/styles/AdminLoginStyles/AdminLogin.css";
import {
  resetSuperAdminPassword,
  resetAdminPassword,
  resetEmployeePassword,
} from "../../services/LoginAuthService/LoginAuthService";

const NewPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({ password: "", confirmPassword: "" });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showConfirmPasswordField, setShowConfirmPasswordField] =
    useState(false);

  const [loading, setLoading] = useState(false); // Loading state for API call
  const navigate = useNavigate();

  // NewPassword.jsx
  const handleSubmit = async (event) => {
    event.preventDefault();
    let validationErrors = {};
    const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

    // Validate password
    if (!password.trim()) {
      validationErrors.password = "This field is required";
    } else if (!passwordRegex.test(password)) {
      validationErrors.password =
        "Password must have at least 8 characters, 1 number, and 1 special character";
    }

    // Validate confirm password
    if (!confirmPassword.trim()) {
      validationErrors.confirmPassword = "This field is required";
    } else if (confirmPassword !== password) {
      validationErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(validationErrors);

    // If there are validation errors, stop here
    if (Object.keys(validationErrors).length !== 0) return;

    setLoading(true); // Start loading

    try {
      // Retrieve the reset_token from localStorage
      const resetToken = localStorage.getItem("resetToken");
      const userType = localStorage.getItem("userType"); // Get stored user type
 

      if (!resetToken) {
        throw new Error("Reset token not found. Please log in again.");
      }

      // Prepare the data for the API call
      const data = {
        new_password: password,
        confirm_password: confirmPassword,
      };

      let response;

      // Determine which API to call based on user type
      if (userType === "superadmin") {
        response = await resetSuperAdminPassword(data, resetToken);
      } else if (userType === "admin") {
        response = await resetAdminPassword(data, resetToken);
      } else if (userType === "employee") {
        response = await resetEmployeePassword(data, resetToken);
      } else {
        throw new Error("Invalid user type for password reset");
      }

      // Store the qr_code_image in localStorage
      if (response.qr_code_image) {
        let qrCodeData = response.qr_code_image;
        let authToken = response.access_token;
        // Check if the response already contains the prefix.
        if (!qrCodeData.startsWith("data:image/png;base64,")) {
          // Check if the string is hex by matching only hex digits.
          if (/^[0-9a-fA-F]+$/.test(qrCodeData)) {
            // Convert hex string to Base64.
            const hexToBase64 = (hex) => {
              const binary = hex
                .match(/.{1,2}/g)
                .map((byte) => String.fromCharCode(parseInt(byte, 16)))
                .join("");
              return btoa(binary);
            };
            qrCodeData = `data:image/png;base64,${hexToBase64(qrCodeData)}`;
          } else {
            // Otherwise, assume it's a Base64 string without the prefix.
            qrCodeData = `data:image/png;base64,${qrCodeData}`;
          }
        }

        // Store the valid data URL.
        localStorage.setItem("qr_code_image", JSON.stringify(qrCodeData));
        localStorage.setItem("access_token", authToken); // Store directly if it's a string
        sessionStorage.setItem("currentpassword", JSON.stringify(data));

      }

      // If no error, handle success
      localStorage.removeItem("resetToken");
      // localStorage.removeItem("userType");
      navigate("/qrcode");
    } catch (error) {
      // Handle errors from API or network
      const errorMessage =
        (typeof error === "object" &&
          (error.non_field_errors?.[0] || error.detail || error.message)) ||
        error ||
        "An error occurred. Please try again.";

      setErrors({ api: errorMessage });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="background w-full h-[100vh] flex items-center">
      <div className="flex justify-center mx-auto w-full h-[50vh] rounded-xl">
        <div className="z-10 relative flex flex-col justify-center w-3/5 px-10">
          <div className="absolute -z-10 inset-0 border-2 border-blood rounded-xl backdrop-blur-sm bg-red-500/50"></div>
          <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 flex justify-center">
            <img
              src={require("../../assets/images/commonImages/ZeroMiddleMan Logo 3.png")}
              alt=""
              className="bg-white px-4 rounded-2xl"
            />
          </div>

          <div className="flex flex-col gap-5">
            {/* New Password Field */}
            <div className="flex flex-col">
              {!showPasswordField && (
                <div
                  className="px-2 flex items-center gap-2 cursor-pointer text-white py-2 outline-none border-b border-white bg-transparent"
                  onClick={() => setShowPasswordField(true)}
                >
                  <span className="text-2xl">
                    <CiLock />         
                  </span>
                  <label className="font-semibold text-lg md:text-xl">
                    New Password
                  </label>
                </div>
              )}
              {showPasswordField && (
                <div className="relative">
                  <input
                    className="px-5 py-2 text-white placeholder-white placeholder:text-lg placeholder:font-semibold outline-none border-b bg-transparent w-full"
                    type={passwordVisible ? "text" : "password"}
                    placeholder="Enter your Password"
                    value={password}
                    autoFocus
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span
                    className="absolute right-3 top-3 text-xl text-white cursor-pointer"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    {passwordVisible ? <AiFillEyeInvisible /> : <AiFillEye />}
                  </span>
                </div>
              )}
              {errors.password && (
                <p className="text-white text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="flex flex-col">
              {!showConfirmPasswordField && (
                <div
                  className="px-2 flex items-center gap-2 cursor-pointer text-white py-2 outline-none border-b border-white bg-transparent"
                  onClick={() => setShowConfirmPasswordField(true)}
                >
                  <span className="text-2xl">
                    <CiLock />
                  </span>
                  <label className="font-semibold text-lg md:text-xl">
                    Confirm Password
                  </label>
                </div>
              )}
              {showConfirmPasswordField && (
                <div className="relative">
                  <input
                    className="px-5 py-2 text-white placeholder-white placeholder:text-lg placeholder:font-semibold outline-none border-b bg-transparent w-full"
                    type={confirmPasswordVisible ? "text" : "password"}
                    placeholder="Confirm your Password"
                    value={confirmPassword}
                    autoFocus
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <span
                    className="absolute right-3 top-3 text-xl text-white cursor-pointer"
                    onClick={() =>
                      setConfirmPasswordVisible(!confirmPasswordVisible)
                    }
                  >
                    {confirmPasswordVisible ? (
                      <AiFillEyeInvisible />
                    ) : (
                      <AiFillEye />
                    )}
                  </span>
                </div>
              )}
              {errors.confirmPassword && (
                <p className="text-white text-sm mt-1">
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center mt-5">
            <button
              className="px-10 py-2 bg-white font-semibold text-xl text-[#ff0000] hover:bg-[#d93030] hover:text-white rounded-md"
              type="button"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Updating..." : "Submit"}
            </button>
          </div>

          {/* Display API errors */}
          {errors.api && (
            <p className="text-white text-sm text-center mt-2">{errors.api}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewPassword;
